import OptimizedImage from "@/components/OptimizedImage";
import { GradientText } from "@/components/ui/gradient-text";
import { SimpleReveal } from "@/hooks/useSimpleAnimation";
import { ChevronDown } from "lucide-react";

interface Hero1Props {
  heading: string;
  description: string;
  image: {
    src: string;
    alt: string;
  };
}

const Hero1 = ({
  heading,
  description,
  image
}: Hero1Props) => {
  const handleScrollDown = () => {
    const hero = document.querySelector('.hero-section-spacing');
    if (hero?.nextElementSibling) {
      hero.nextElementSibling.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section className="relative bg-white pt-2 md:pt-20 lg:pt-28 pb-6 md:pb-20 lg:pb-24">

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Mobile/Tablet: Stack vertically, Desktop: Side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-6 xl:gap-7 2xl:gap-8 items-start lg:items-center">
          
          {/* Text Content */}
          <div className="order-2 lg:order-1">
            <div className="space-y-6 text-center lg:text-left max-w-2xl lg:max-w-none mx-auto lg:mx-0">
              {/* Red accent line */}
              <SimpleReveal>
                <div className="flex justify-center lg:justify-start">
                  <div className="h-0.5 bg-gradient-to-r from-[hsl(var(--color-1))] via-[hsl(var(--color-3))] to-[hsl(var(--color-1))] animate-hero-accent-grow" />
                </div>
              </SimpleReveal>

              {/* Staggered heading lines */}
              <h1 className="text-neutral-900 leading-tight font-semibold tracking-tight text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-6xl break-words">
                {/* Mobile: single block for natural text flow */}
                <span className="block md:hidden">
                  The pharmaceutical industry needs better oral delivery solutions for poorly water‑soluble drugs and biopharmaceuticals
                </span>
                {/* Desktop: staggered line-by-line reveal */}
                <span className="hidden md:block">
                  <SimpleReveal delay={0}>
                    <span className="block md:whitespace-nowrap">The pharmaceutical industry </span>
                  </SimpleReveal>
                  <SimpleReveal delay={150}>
                    <span className="block md:whitespace-nowrap">needs better oral delivery solutions </span>
                  </SimpleReveal>
                  <SimpleReveal delay={300}>
                    <span className="block md:whitespace-nowrap">for poorly water‑soluble drugs </span>
                  </SimpleReveal>
                  <SimpleReveal delay={450}>
                    <span className="block md:whitespace-nowrap">and biopharmaceuticals</span>
                  </SimpleReveal>
                </span>
              </h1>

              {/* Gradient tagline with delay */}
              <SimpleReveal delay={600}>
                <GradientText
                  colors={["hsl(var(--color-1))", "hsl(var(--color-2))", "hsl(var(--color-3))", "hsl(var(--color-4))", "hsl(var(--color-5))", "hsl(var(--color-1))", "hsl(var(--color-2))", "hsl(var(--color-3))", "hsl(var(--color-4))", "hsl(var(--color-5))", "hsl(var(--color-1))", "hsl(var(--color-2))"]}
                  animationSpeed={18}
                  className="font-semibold leading-tight text-2xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl 2xl:text-5xl"
                >
                  {description}
                </GradientText>
              </SimpleReveal>
            </div>
          </div>
          
          {/* Image with floating + slow rotation */}
          <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
            <div className="w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-4xl">
              <div className="animate-hero-float">
                <div className="animate-hero-spin aspect-[4/3]">
                  <OptimizedImage 
                    src={image.src} 
                    alt={image.alt} 
                    className="w-full h-full object-contain drop-shadow-xl" 
                    sizes="(max-width: 767px) 90vw, (max-width: 1023px) 80vw, 50vw"
                    priority={true} 
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll-down indicator (desktop only) */}
      <button
        onClick={handleScrollDown}
        className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-1 text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer z-10"
        aria-label="Scroll to next section"
      >
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </button>
    </section>
  );
};
export { Hero1 };
